import { Box, Container, Grid, Paper } from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, FieldErrors, FieldValues, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import styles from '../../../assets/styles/signup';
import CustomButton from '../../../components/Button/button';
import ShowPassword from '../../../components/showPassword/ShowPassword';
import CustomTextField from '../../../components/TextField/textField';
import CustomTypography from '../../../components/Typography/typography';
import { signup } from '../../../redux/slice/authSlice';
import logo from '../../../assets/images/logo1.png';

const SignUp = () => {
  const [showPassword, setShowPassword] = useState(false);
  const classes = styles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const signupData = useSelector((state: any) => state.auth.signupData);
  const form = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
    },
  });
  const { handleSubmit, control, formState, clearErrors } = form;
  const { errors, isSubmitting } = formState;

  //submit method that calls signup api
  const onSubmit = (data: FieldValues) => {
    const newData = { ...data, email: data.email.toLowerCase() };
    dispatch(signup(newData));
  };

  const onError = (errors: FieldErrors) => {};

  useEffect(() => {
    signupData?.result?._id && navigate('/dashboard');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signupData]);

  return (
    <>
      <Container maxWidth='sm' className={classes.containerRoot}>
        <Box className={classes.Box}>
          <Paper className={classes.headingText} elevation={5}>
            <div className='heading-container'>
              <div>
                <CustomTypography variant='h3'>Sign Up</CustomTypography>
                <CustomTypography
                  variant='body2'
                  align='center'
                  className='custom-margin'
                >
                  Already a member?
                  <Link className='customAuthLinkStyle' to='/'>
                    {' '}
                    Sign In here
                  </Link>
                </CustomTypography>
              </div>
              <img alt='logo' src={logo} width={80} className='logo-image' />
            </div>
            <form onSubmit={handleSubmit(onSubmit, onError)}>
              <Grid container className={classes.GridContainer} spacing={1}>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name='firstName'
                    control={control}
                    render={({ field }) => (
                      <CustomTextField
                        {...field}
                        label='First Name'
                        size='medium'
                        fullWidth
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          field.onChange(e.target.value, clearErrors('root'));
                        }}
                      />
                    )}
                    rules={{
                      required: {
                        value: true,
                        message: 'Please enter a first name',
                      },
                      validate: {
                        whitespace: (fieldValue: string) => {
                          return (
                            /^[ \t]+/.test(fieldValue) !== true ||
                            "First name can't be whitespace"
                          );
                        },
                      },
                    }}
                  />
                  <CustomTypography
                    variant='body2'
                    color={'red'}
                    align='justify'
                  >
                    {errors.firstName?.message?.toString()}
                  </CustomTypography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name='lastName'
                    control={control}
                    render={({ field }) => (
                      <CustomTextField
                        {...field}
                        label='Last Name'
                        fullWidth
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          field.onChange(e.target.value, clearErrors('root'));
                        }}
                      />
                    )}
                    rules={{
                      required: {
                        value: true,
                        message: 'Please enter a last name',
                      },
                      validate: {
                        whitespace: (fieldValue: string) => {
                          return (
                            /^[ \t]+/.test(fieldValue) !== true ||
                            "Last name can't be whitespace"
                          );
                        },
                      },
                    }}
                  />
                  <CustomTypography
                    variant='body2'
                    color={'red'}
                    align='justify'
                  >
                    {errors.lastName?.message?.toString()}
                  </CustomTypography>
                </Grid>

                <Grid item xs={12} sm={12}>
                  <Controller
                    name='email'
                    control={control}
                    render={({ field }) => (
                      <CustomTextField
                        {...field}
                        label='Email'
                        fullWidth
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          field.onChange(e.target.value, clearErrors('root'));
                        }}
                      />
                    )}
                    rules={{
                      required: {
                        value: true,
                        message: 'Please enter a email',
                      },
                      pattern: {
                        value: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g,
                        message: 'Please enter valid Email',
                      },
                      validate: {
                        whitespace: (fieldValue: string) => {
                          return (
                            /^[ \t]+/.test(fieldValue) !== true ||
                            "Email can't be whitespace"
                          );
                        },
                      },
                    }}
                  />
                  <CustomTypography
                    variant='body2'
                    color={'red'}
                    align='justify'
                  >
                    {errors.email?.message?.toString()}
                  </CustomTypography>
                </Grid>
                <Grid item xs={12} sm={12}>
                  <Controller
                    name='password'
                    control={control}
                    render={({ field }) => (
                      <div className={classes.textInputContainer}>
                        <CustomTextField
                          {...field}
                          label='Password'
                          type={showPassword ? 'text' : 'password'}
                          fullWidth
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            field.onChange(e.target.value, clearErrors('root'));
                          }}
                        />
                        <ShowPassword
                          showPassword={showPassword}
                          onClick={() => setShowPassword(!showPassword)}
                        />
                      </div>
                    )}
                    rules={{
                      required: {
                        value: true,
                        message: 'Please enter a password',
                      },
                      minLength: {
                        value: 6,
                        message: 'Password must be 6 character',
                      },
                      maxLength: {
                        value: 20,
                        message: 'Password must be less than 20 character',
                      },
                      validate: {
                        whitespace: (fieldValue: string) => {
                          return (
                            /^[ \t]+/.test(fieldValue) !== true ||
                            "Password can't be whitespace"
                          );
                        },
                      },
                    }}
                  />
                  <CustomTypography variant='body2' color='red' align='justify'>
                    {errors.password?.message?.toString()}
                  </CustomTypography>
                </Grid>
                <CustomButton
                  sx={{ mt: 2 }}
                  type='submit'
                  fullWidth
                  color='primary'
                  disabled={isSubmitting}
                >
                  Sign Up{' '}
                </CustomButton>
              </Grid>
            </form>

            {/* <CustomTypography align="right" className={classes.GridContainer}>
							<Link className="customAuthLinkStyle" to="/forgotpassword">
								<CustomButton variant="text" color="primary" sx={{ mt: 2 }}>
									Forgot password?
								</CustomButton>
							</Link>
						</CustomTypography> */}
          </Paper>
        </Box>
      </Container>
    </>
  );
};

export default SignUp;
